<template>
  <template v-if="$route.name === 'attendances'">
    <AttendanceFilters
      :classes="classes"
      :teacher-classes="teacherClasses"
      :filters="filters"
      @filter="handleFilter"
      v-if="!isGuardian"
      v-can="'student_attendance.display'"
    />

    <div class="col-12 col-xl-12">
      <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
              {{ isGuardian ? t('attendance.guardian_title') : t('attendance.title') }}
            </h6>
          </div>
        </div>

        <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
          <div class="table-responsive p-1 p-xl-4">
            <LoadingSpinner
              v-if="isLoading && attendances.length === 0"
              :message="t('attendance.loading')"
              class="py-5"
            />

            <template v-else>
              <div class="d-block d-xl-none">
                <div v-if="!isLoading && attendances.length === 0" class="text-center py-3 text-muted">
                  <i class="fas fa-users-slash fa-2x mb-2"></i>
                  <p>{{ t('attendance.no_records') }}</p>
                </div>

                <div v-for="attendance in attendances" :key="'mobile-' + attendance.id" class="card mb-3 mx-2">
                  <div class="card-body py-2">
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <h6 class="mb-0 text-sm">{{ attendance.student.name }}</h6>
                        <p class="text-xs text-muted mb-0">
                          {{ formatDate(attendance.date) }} - {{ getDayName(attendance.day) }}
                        </p>
                      </div>
                      <div>
                        <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                          {{ getStatusName(attendance.status) }}
                        </span>
                      </div>
                    </div>

                    <div class="d-flex justify-content-end align-items-center mt-2">
                      <div class="d-inline-flex" v-if="!isTeacher && !isGuardian">
                        <router-link
                          :to="`/attendances/edit/${attendance.id}`"
                          class="btn btn-link text-secondary p-0 mx-1"
                          :title="t('attendance.edit')"
                          v-can="'student_attendance.update'"
                        >
                          <i class="fas fa-edit fa-xs"></i>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <table class="table align-items-center mb-0 d-none d-xl-table">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.student_name') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.date') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('attendance.day') }}</th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ t('attendance.status') }}</th>
                    <th
                      v-if="!isTeacher && !isGuardian"
                      v-can="'student_attendance.update'"
                      class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4"
                    >
                      {{ t('attendance.actions') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!isLoading && attendances.length === 0">
                    <td colspan="5" class="text-center py-4 text-muted">
                      <i class="fas fa-users-slash fa-2x mb-2"></i>
                      <p>{{ t('attendance.no_records') }}</p>
                    </td>
                  </tr>

                  <template v-if="!isLoading">
                    <tr v-for="attendance in attendances" :key="attendance.id">
                      <td>
                        <div class="d-flex px-3 py-1">
                          <div class="d-flex flex-column justify-content-center">
                            <h6 class="mb-0 text-md">{{ attendance.student.name }}</h6>
                          </div>
                        </div>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          {{ formatDate(attendance.date) }}
                        </p>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          {{ getDayName(attendance.day) }}
                        </p>
                      </td>
                      <td class="align-middle text-center">
                        <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                          {{ getStatusName(attendance.status) }}
                        </span>
                      </td>
                      <td class="align-middle text-center pe-4" v-if="!isTeacher && !isGuardian" v-can="'student_attendance.update'">
                        <div class="d-inline-flex">
                          <router-link
                            :to="`/attendances/edit/${attendance.id}`"
                            class="btn btn-link text-secondary p-1 mx-1"
                            :title="t('attendance.edit')"
                            v-can="'student_attendance.update'"
                          >
                            <i class="fas fa-edit fa-sm"></i>
                          </router-link>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>

              <div class="d-flex justify-content-center mt-4">
                <Pagination
                  :pagination="pagination"
                  @page-changed="onPageChanged"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </template>

  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useToast } from 'vue-toastification';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import AttendanceFilters from "@/components/features/students/filters/AttendanceFilters.vue";
import api from "@/api";
import { useStore } from 'vuex';
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const toast = useToast();
const store = useStore();

const attendances = ref([]);
const isLoading = ref(false);
const classes = ref([]);
const teacherClasses = ref([]);

const currentUser = computed(() => store.state.user);
const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');
const isGuardian = computed(() => currentUser.value?.role?.slug === 'guardian');

const pagination = ref({
  currentPage: 1,
  perPage: 15,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0
});

const filters = ref({
  date: '',
  status: '',
  class_id: '',
  search: ''
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('ar', options);
};

const getDayName = (day) => {
  if (!day) return '';

  const days = {
    sunday: t('attendance.sunday'),
    monday: t('attendance.monday'),
    tuesday: t('attendance.tuesday'),
    wednesday: t('attendance.wednesday'),
    thursday: t('attendance.thursday'),
    friday: t('attendance.friday'),
    saturday: t('attendance.saturday')
  };

  return days[String(day).toLowerCase()] || day;
};

const getStatusName = (status) => {
  const statuses = {
    present: t('attendance.present'),
    absent: t('attendance.absent'),
    late: t('attendance.late'),
    excused: t('attendance.excused')
  };
  return statuses[status] || status;
};

const getStatusBadgeClass = (status) => {
  const classesMap = {
    present: 'bg-success',
    absent: 'bg-danger',
    late: 'bg-warning',
    excused: 'bg-info'
  };
  return classesMap[status] || 'bg-secondary';
};

const fetchAttendances = async () => {
  isLoading.value = true;

  try {
    const params = {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage,
      ...filters.value
    };

    if (isTeacher.value && currentUser.value?.id) {
      params.teacher_id = currentUser.value.id;
    }

    if (isGuardian.value && currentUser.value?.id) {
      params.guardian_id = currentUser.value.id;

      if (store.state.guardian?.guardianStudents?.length > 0) {
        params.student_ids = store.state.guardian.guardianStudents.map(s => s.id).join(',');
      }
    }

    Object.keys(params).forEach((key) => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const response = await api.get('/attendances', { params });
    attendances.value = response.data.data || [];

    if (response.data.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to
      };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || t('attendance.load_failed');
    toast.error(errorMsg);
  } finally {
    isLoading.value = false;
  }
};

const fetchClasses = async () => {
  if (isGuardian.value) return;

  try {
    if (isTeacher.value) {
      const response = await api.get(`/teachers/${currentUser.value.id}/classes`);
      teacherClasses.value = response.data.data || [];

      if (teacherClasses.value.length === 1 && !filters.value.class_id) {
        filters.value.class_id = teacherClasses.value[0].id;
      }
    } else {
  const response = await api.get('/classes', {
    params: {
      all: true
    }
  });      classes.value = response.data.data || [];
    }
  } catch (error) {
    console.error("Error fetching classes:", error);
  }
};

const fetchGuardianStudents = async () => {
  if (!isGuardian.value || !currentUser.value?.id) return;

  try {
    await store.dispatch('guardian/fetchGuardianStudents', currentUser.value.id);
  } catch (error) {
    console.error("Error fetching guardian students:", error);
  }
};

const handleFilter = (newFilters) => {
  filters.value = {
    ...filters.value,
    ...newFilters
  };

  pagination.value.currentPage = 1;
  fetchAttendances();
};

const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchAttendances();
};

onMounted(async () => {
  await fetchGuardianStudents();
  await fetchClasses();
  await fetchAttendances();
});
</script>